import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  Typography,
  CircularProgress,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';

interface AddBusinessDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface RegisterFormData {
  name: string;
  abn: string;
  street: string;
  city: string;
  state: string;
  postCode: string;
  country: string;
}

const emptyForm: RegisterFormData = {
  name: '',
  abn: '',
  street: '',
  city: '',
  state: '',
  postCode: '',
  country: 'Australia',
};

const AddBusinessDialog: React.FC<AddBusinessDialogProps> = ({ open, onClose, onSuccess }) => {
  const auth = useAuth();
  const authHeader = { Authorization: `Bearer ${auth.tokens?.accessToken}` };
  const [formData, setFormData] = useState<RegisterFormData>(emptyForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetAll = () => {
    setFormData(emptyForm);
    setError(null);
  };

  const handleChange = (field: keyof RegisterFormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [field]: e.target.value });
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.abn && !/^\d{11}$/.test(formData.abn)) {
      setError('ABN must be exactly 11 digits');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/businesses/self-register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeader,
        },
        body: JSON.stringify({
          name: formData.name,
          abn: formData.abn,
          primaryUserId: auth.user?.id,
          address: formData.street || formData.city ? {
            street: formData.street,
            city: formData.city,
            state: formData.state,
            postCode: formData.postCode,
            country: formData.country,
          } : null,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create business');
      }

      onSuccess();
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    resetAll();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Your Business</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <form id="register-business-form" onSubmit={handleSubmit}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Register your business details to get started right away.
            </Typography>

            <TextField
              fullWidth
              required
              label="Business Name"
              value={formData.name}
              onChange={handleChange('name')}
              disabled={loading}
              sx={{ mb: 2 }}
              inputProps={{ maxLength: 255 }}
            />

            <TextField
              fullWidth
              label="ABN (11 digits, optional)"
              value={formData.abn}
              onChange={handleChange('abn')}
              disabled={loading}
              sx={{ mb: 2 }}
              placeholder="12345678901"
              helperText="Australian Business Number (11 digits, no spaces) — optional"
              inputProps={{ maxLength: 11 }}
            />

            <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
              Business Address (Optional)
            </Typography>

            <TextField
              fullWidth
              label="Street Address"
              value={formData.street}
              onChange={handleChange('street')}
              disabled={loading}
              sx={{ mb: 2 }}
              inputProps={{ maxLength: 255 }}
            />

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2 }}>
              <TextField
                fullWidth
                label="City"
                value={formData.city}
                onChange={handleChange('city')}
                disabled={loading}
                inputProps={{ maxLength: 100 }}
              />
              <TextField
                fullWidth
                label="State"
                value={formData.state}
                onChange={handleChange('state')}
                disabled={loading}
                inputProps={{ maxLength: 50 }}
              />
            </Box>

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              <TextField
                label="Post Code"
                value={formData.postCode}
                onChange={handleChange('postCode')}
                disabled={loading}
                inputProps={{ maxLength: 10 }}
                sx={{ width: { xs: '100%', sm: 150 } }}
              />
              <TextField
                fullWidth
                label="Country"
                value={formData.country}
                onChange={handleChange('country')}
                disabled={loading}
                inputProps={{ maxLength: 100 }}
              />
            </Box>
          </form>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          type="submit"
          form="register-business-form"
          variant="contained"
          disabled={loading || !formData.name}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Creating...' : 'Create Business'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddBusinessDialog;
